import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Plus, Edit2, Trash2, ChevronDown, ChevronUp, RefreshCw, X, RotateCcw } from 'lucide-react';
import { profilesAPI, tenantsAPI } from '../api';
import { Card, Button, Badge, Modal, Input, Select, PageHeader, Alert, Pagination, useSort } from '../components/UI';
import DurationInput from '../components/DurationInput';
import { useTenant } from '../TenantContext';
import { useAuth } from '../context/AuthContext';

const MenuItem = ({ children, onClick, color, loading }) => (
  <button onClick={onClick} disabled={loading} style={{
    display: 'block', width: '100%', textAlign: 'left',
    padding: '9px 14px', background: 'none', border: 'none',
    cursor: loading ? 'wait' : 'pointer', fontSize: 13,
    color: color || 'var(--text-secondary)',
    transition: 'background 0.1s',
  }}
    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
    onMouseLeave={e => e.currentTarget.style.background = 'none'}
  >{children}</button>
);

const DropdownPortal = ({ anchorRef, open, children }) => {
  const [pos, setPos] = React.useState({ top: 0, left: 0 });
  React.useEffect(() => {
    if (open && anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + window.scrollY + 4, left: rect.right + window.scrollX });
    }
  }, [open, anchorRef]);
  if (!open) return null;
  return ReactDOM.createPortal(
    <div style={{ position: 'absolute', top: pos.top, left: pos.left, transform: 'translateX(-100%)', zIndex: 9999, background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow)', minWidth: 160, overflow: 'hidden' }}
      onClick={e => e.stopPropagation()}>
      {children}
    </div>,
    document.body
  );
};


const EMPTY_FORM = {
  name: '', description: '', profile_type: 'regular', session_timeout: '', interim_interval: '',
  upload_limit_m: '', download_limit_m: '', expired_duration: '', status: 'active',
  service_type: '', shared_user: '', suspended_profile_id: '', expired_profile_id: '',
  disconnect_user: false, custom_attrs: [],
};
const EMPTY_ATTR = { attribute: '', op: ':=', value: '', type: 'reply' };

const UNIT_MAP = { m: 'menit', h: 'jam', d: 'hari', w: 'minggu', M: 'bulan' };
function formatDuration(str) {
  if (!str) return '—';
  const match = String(str).match(/^(\d+)([mhdwM])$/);
  if (!match) return str;
  const [, n, u] = match;
  return `${n} ${UNIT_MAP[u] || u}`;
}

const PROFILE_TYPE_COLOR = {
  regular:   { bg: 'rgba(59,130,246,0.12)',  color: 'var(--accent-blue)'   },
  suspended: { bg: 'rgba(245,158,11,0.12)',  color: 'var(--accent-orange)' },
  expired:   { bg: 'rgba(239,68,68,0.12)',   color: 'var(--accent-red)'    },
};
const ProfileTypeBadge = ({ type }) => {
  const s = PROFILE_TYPE_COLOR[type] || { bg: 'var(--bg-input)', color: 'var(--text-muted)' };
  return <span style={{ fontSize: 11, padding: '2px 8px', background: s.bg, color: s.color, borderRadius: 20, border: `1px solid ${s.color}44`, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{type || '—'}</span>;
};

const SyncBadge = ({ status }) => {
  const map = { synced: { color: 'var(--accent-green)', bg: 'rgba(16,185,129,0.12)', label: 'synced' }, pending: { color: 'var(--accent-orange)', bg: 'rgba(245,158,11,0.12)', label: 'pending' }, error: { color: 'var(--accent-red)', bg: 'rgba(239,68,68,0.12)', label: 'error' } };
  const s = map[status] || { color: 'var(--text-muted)', bg: 'var(--bg-input)', label: status || '—' };
  return <span style={{ fontSize: 11, padding: '2px 8px', background: s.bg, color: s.color, borderRadius: 20, border: `1px solid ${s.color}44` }}>{s.label}</span>;
};

const ProfileMenuButton = ({ p, openMenuId, setOpenMenuId, onlyDeleted, syncingId, handleRestore, handleSync, openEdit, setDeleteConfirm, setDeletePermanent, isSuperadmin }) => {
  const btnRef = React.useRef(null);
  return (
    <div>
      <button ref={btnRef}
        onClick={e => { e.stopPropagation(); setOpenMenuId(openMenuId === p.id ? null : p.id); }}
        style={{ background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '4px 10px', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 16, lineHeight: 1 }}>
        ···
      </button>
      <DropdownPortal anchorRef={btnRef} open={openMenuId === p.id}>
        {onlyDeleted ? (
          <MenuItem color="var(--accent-green)" onClick={() => { handleRestore(p); setOpenMenuId(null); }}>↩ Restore</MenuItem>
        ) : (<>
          <MenuItem loading={syncingId === p.id} onClick={() => { handleSync(p); setOpenMenuId(null); }}>🔄 Sync</MenuItem>
          <MenuItem onClick={() => { openEdit(p); setOpenMenuId(null); }}>✏️ Edit</MenuItem>
        </>)}
        {(!onlyDeleted || isSuperadmin) && (<>
          <div style={{ borderTop: '1px solid var(--border)' }} />
          <MenuItem color="var(--accent-red)" onClick={() => { setDeleteConfirm(p); setDeletePermanent(false); setOpenMenuId(null); }}>🗑 Hapus</MenuItem>
        </>)}
      </DropdownPortal>
    </div>
  );
};

export default function Profiles() {
  const { selectedTenant } = useTenant();
  const { user } = useAuth();
  const isSuperadmin = user?.role === 'superadmin';
  const [profiles, setProfiles] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [onlyDeleted, setOnlyDeleted] = useState(false);
  const [profileTypeFilter, setProfileTypeFilter] = useState('');
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deletePermanent, setDeletePermanent] = useState(false);
  const [selected, setSelected] = useState([]);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [bulkDeletePermanent, setBulkDeletePermanent] = useState(false);
  const [bulkSyncing, setBulkSyncing] = useState(false);
  const [bulkUpdateModal, setBulkUpdateModal] = useState(false);
  const [bulkUpdateForm, setBulkUpdateForm] = useState({ status: '', upload_limit_m: '', download_limit_m: '', session_timeout: '', interim_interval: '', expired_duration: '', description: '' });
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [syncingId, setSyncingId] = useState(null);
  const [perPage, setPerPage] = useState(20);
  const [expandedId, setExpandedId] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);

  useEffect(() => {
    if (!openMenuId) return;
    const handler = () => setOpenMenuId(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [openMenuId]);

  // Load profiles by type for suspended/expired selects
  const [suspendedProfiles, setSuspendedProfiles] = useState([]);
  const [expiredProfiles, setExpiredProfiles] = useState([]);

  useEffect(() => {
    if (!selectedTenant) { setSuspendedProfiles([]); setExpiredProfiles([]); return; }
    profilesAPI.list(selectedTenant, { per_page: 100, status: 'active', profile_type: 'suspended' })
      .then(r => setSuspendedProfiles(r.profiles || [])).catch(() => {});
    profilesAPI.list(selectedTenant, { per_page: 100, status: 'active', profile_type: 'expired' })
      .then(r => setExpiredProfiles(r.profiles || [])).catch(() => {});
  }, [selectedTenant]);

  const { sort, onSortChange, sortParam } = useSort();

  const load = useCallback(async () => {
    if (!selectedTenant) { setProfiles([]); return; }
    setLoading(true); setError(null);
    try {
      const params = { page, per_page: perPage };
      if (onlyDeleted) params.only_deleted = true;
      if (profileTypeFilter) params.profile_type = profileTypeFilter;
      if (q) params.q = q;
      if (statusFilter) params.status = statusFilter; // bisa comma-separated
      if (sortParam) params.sort = sortParam;
      const res = await profilesAPI.list(selectedTenant, params);
      setProfiles(res.profiles || []);
      setTotal(res.total || 0);
      setTotalPages(res.total_pages || 1);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [selectedTenant, page, onlyDeleted, profileTypeFilter, q, statusFilter, perPage, sortParam]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); setSelected([]); }, [selectedTenant, onlyDeleted, profileTypeFilter, q, statusFilter]);

  const openCreate = () => {
    if (!selectedTenant) { setError('Pilih tenant terlebih dahulu.'); return; }
    setEditTarget(null); setForm(EMPTY_FORM); setFormError(null); setModalOpen(true);
  };
  const openEdit = (p) => {
    setEditTarget(p);
    setForm({
      name: p.name || '', description: p.description || '', profile_type: p.profile_type || 'regular',
      session_timeout: p.session_timeout ?? '', interim_interval: p.interim_interval ?? '', upload_limit_m: p.upload_limit_m ?? '', download_limit_m: p.download_limit_m ?? '',
      expired_duration: p.expired_duration || '', status: p.status || 'active',
      service_type: p.service_type || '', shared_user: p.shared_user ?? '',
      suspended_profile_id: p.suspended_profile_id ?? '', expired_profile_id: p.expired_profile_id ?? '',
      disconnect_user: false,
      custom_attrs: Array.isArray(p.custom_attrs) ? p.custom_attrs.map(a => ({ attribute: a.attribute || '', op: a.op || ':=', value: a.value || '', type: a.type || 'reply' })) : [],
    });
    setFormError(null); setModalOpen(true);
  };

  const handleSave = async () => {
    setSaving(true); setFormError(null);
    try {
      const payload = { name: form.name, status: form.status, profile_type: form.profile_type || 'regular', custom_attrs: form.custom_attrs.length > 0 ? form.custom_attrs : [] };
      if (form.description) payload.description = form.description;
      if (form.upload_limit_m !== '' && form.upload_limit_m !== null) payload.upload_limit_m = Number(form.upload_limit_m);
      if (form.download_limit_m !== '' && form.download_limit_m !== null) payload.download_limit_m = Number(form.download_limit_m);
      if (form.expired_duration) payload.expired_duration = form.expired_duration;
      if (form.session_timeout !== '' && form.session_timeout !== null) payload.session_timeout = Number(form.session_timeout);
      if (form.interim_interval !== '' && form.interim_interval !== null) payload.interim_interval = Number(form.interim_interval);
      if (form.service_type) payload.service_type = form.service_type;
      if (form.shared_user !== '') payload.shared_user = Number(form.shared_user);
      if (form.suspended_profile_id !== '') payload.suspended_profile_id = Number(form.suspended_profile_id);
      if (form.expired_profile_id !== '') payload.expired_profile_id = Number(form.expired_profile_id);
      if (editTarget) payload.disconnect_user = form.disconnect_user || false;
      if (editTarget) await profilesAPI.update(selectedTenant, editTarget.id, payload);
      else await profilesAPI.create(selectedTenant, payload);
      setModalOpen(false); setSuccess(editTarget ? 'Profile diperbarui.' : 'Profile dibuat.'); load();
    } catch (e) { setFormError(e.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    const permanent = isSuperadmin && (onlyDeleted || deletePermanent);
    try {
      if (permanent) await profilesAPI.deletePermanent(selectedTenant, deleteConfirm.id);
      else await profilesAPI.delete(selectedTenant, deleteConfirm.id);
      setDeleteConfirm(null); setDeletePermanent(false);
      setSuccess(permanent ? 'Profile dihapus permanen.' : 'Profile dihapus.'); load();
    } catch (e) { setError(e.message); setDeleteConfirm(null); }
  };

  const handleRestore = async (p) => {
    try { await profilesAPI.restore(selectedTenant, p.id); setSuccess('Profile dipulihkan.'); load(); }
    catch (e) { setError(e.message); }
  };

  const handleBulkDelete = async () => {
    const permanent = isSuperadmin && (onlyDeleted || bulkDeletePermanent);
    try {
      if (permanent) await Promise.all(selected.map(id => profilesAPI.deletePermanent(selectedTenant, id)));
      else await Promise.all(selected.map(id => profilesAPI.delete(selectedTenant, id)));
      setSuccess(`${selected.length} profile ${permanent ? 'dihapus permanen' : 'dihapus'}.`);
      setSelected([]); setBulkDeleteConfirm(false); setBulkDeletePermanent(false); load();
    } catch (e) { setError(e.message); setBulkDeleteConfirm(false); }
  };

  const handleBulkRestore = async () => {
    try {
      await Promise.all(selected.map(id => profilesAPI.restore(selectedTenant, id)));
      setSuccess(`${selected.length} profile dipulihkan.`); setSelected([]); load();
    } catch (e) { setError(e.message); }
  };

  const handleBulkSync = async () => {
    setBulkSyncing(true);
    try { await Promise.all(selected.map(id => profilesAPI.sync(selectedTenant, id))); setSuccess(`${selected.length} profile di-sync.`); setSelected([]); }
    catch (e) { setError(e.message); } finally { setBulkSyncing(false); }
  };

  const handleBulkUpdate = async () => {
    setBulkUpdating(true);
    try {
      const payload = {};
      if (bulkUpdateForm.status) payload.status = bulkUpdateForm.status;
      if (bulkUpdateForm.upload_limit_m !== '') payload.upload_limit_m = Number(bulkUpdateForm.upload_limit_m);
      if (bulkUpdateForm.download_limit_m !== '') payload.download_limit_m = Number(bulkUpdateForm.download_limit_m);
      if (bulkUpdateForm.session_timeout) payload.session_timeout = Number(bulkUpdateForm.session_timeout);
      if (bulkUpdateForm.interim_interval) payload.interim_interval = Number(bulkUpdateForm.interim_interval);
      if (bulkUpdateForm.expired_duration) payload.expired_duration = bulkUpdateForm.expired_duration;
      if (bulkUpdateForm.description) payload.description = bulkUpdateForm.description;
      if (Object.keys(payload).length === 0) { setError('Pilih minimal satu field.'); setBulkUpdating(false); return; }
      await Promise.all(selected.map(id => profilesAPI.update(selectedTenant, id, payload)));
      setSuccess(`${selected.length} profile diperbarui.`); setSelected([]); setBulkUpdateModal(false);
      setBulkUpdateForm({ status: '', upload_limit_m: '', download_limit_m: '', session_timeout: '', interim_interval: '', expired_duration: '', description: '' }); load();
    } catch (e) { setError(e.message); } finally { setBulkUpdating(false); }
  };

  const handleSync = async (p) => {
    setSyncingId(p.id);
    try { await profilesAPI.sync(selectedTenant, p.id); setSuccess(`Profile "${p.name}" di-sync.`); load(); }
    catch (e) { setError(e.message); } finally { setSyncingId(null); }
  };

  const sf = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const addAttr = () => setForm(f => ({ ...f, custom_attrs: [...f.custom_attrs, { ...EMPTY_ATTR }] }));
  const removeAttr = (i) => setForm(f => ({ ...f, custom_attrs: f.custom_attrs.filter((_, idx) => idx !== i) }));
  const updateAttr = (i, k, v) => setForm(f => ({ ...f, custom_attrs: f.custom_attrs.map((a, idx) => idx === i ? { ...a, [k]: v } : a) }));

  const cbStyle = { width: 15, height: 15, cursor: 'pointer', accentColor: 'var(--accent-blue)' };
  const allChecked = profiles.length > 0 && profiles.every(p => selected.includes(p.id));
  const someChecked = !allChecked && profiles.some(p => selected.includes(p.id));

  return (
    <div className="animate-in">
      <PageHeader
        title="Profiles"
        subtitle={`${total} profile${onlyDeleted ? ' (terhapus)' : ''}`}
        actions={
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {selected.length > 0 && (<>
              {!onlyDeleted && <>
                <Button variant="ghost" loading={bulkSyncing} onClick={handleBulkSync}><RefreshCw size={13} /> Sync {selected.length}</Button>
                <Button variant="secondary" onClick={() => setBulkUpdateModal(true)}><Edit2 size={13} /> Update {selected.length}</Button>
              </>}
              {onlyDeleted && <Button variant="success" onClick={handleBulkRestore}><RotateCcw size={13} /> Restore {selected.length}</Button>}
              {(!onlyDeleted || isSuperadmin) && (
                <Button variant="danger" onClick={() => setBulkDeleteConfirm(true)}><Trash2 size={13} /> Hapus {selected.length}</Button>
              )}
            </>)}
            <Button variant={onlyDeleted ? 'secondary' : 'ghost'} onClick={() => setOnlyDeleted(d => !d)}>
              {onlyDeleted ? 'Tampil Aktif' : '🗑 Terhapus'}
            </Button>
            <Button onClick={openCreate} disabled={!selectedTenant}><Plus size={14} /> Tambah Profile</Button>
          </div>
        }
      />

      {error && <Alert type="error" message={error} onClose={() => setError(null)} />}
      {success && <Alert type="success" message={success} onClose={() => setSuccess(null)} />}

      {/* Search & Filter */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <input placeholder="Cari nama profile…" value={q} onChange={e => { setQ(e.target.value); setPage(1); }}
          style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '8px 14px', color: 'var(--text-primary)', fontSize: 13, outline: 'none', fontFamily: 'var(--font-sans)', width: 220 }} />
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
          style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '8px 12px', color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer', outline: 'none' }}>
          <option value="">Semua Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        <div style={{ display: 'flex', gap: 4, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 4 }}>
          {[['','Semua'],['regular','Regular'],['suspended','Suspended'],['expired','Expired']].map(([val, label]) => (
            <button key={val} onClick={() => setProfileTypeFilter(val)}
              style={{ padding: '4px 12px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: profileTypeFilter === val ? 600 : 400, background: profileTypeFilter === val ? 'rgba(59,130,246,0.15)' : 'transparent', color: profileTypeFilter === val ? 'var(--accent-blue)' : 'var(--text-muted)', transition: 'all 0.15s' }}>
              {label}
            </button>
          ))}
        </div>
      </div>

      <Card style={{ padding: 0 }}>
        {loading ? (
          <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[1,2,3].map(i => <div key={i} className="skeleton" style={{ height: 40 }} />)}
          </div>
        ) : profiles.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 16px', color: 'var(--text-muted)', fontSize: 13 }}>
            {selectedTenant ? (onlyDeleted ? 'Tidak ada data terhapus' : 'Belum ada profile') : 'Pilih tenant terlebih dahulu'}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 750 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  <th style={{ padding: '10px 12px', width: 36 }}>
                    <input type="checkbox" style={cbStyle} checked={allChecked}
                      ref={el => { if (el) el.indeterminate = someChecked; }}
                      onChange={() => setSelected(allChecked ? [] : profiles.map(p => p.id))} />
                  </th>
                  {[
                    { label: 'Name', key: 'name' },
                    { label: 'Type', key: 'profile_type' },
                    { label: 'Upload (MB)', key: 'upload_limit_m' },
                    { label: 'Download (MB)', key: 'download_limit_m' },
                    { label: 'Session T/O', key: 'session_timeout' },
                    { label: 'Exp. Duration', key: 'expired_duration' },
                    { label: 'Attrs', key: null },
                    { label: 'Status', key: 'status' },
                    { label: 'Sync', key: 'sync_status' },
                    { label: 'Actions', key: null },
                  ].map(h => {
                    const active = h.key && sort?.key === h.key;
                    return (
                      <th key={h.label} style={{ padding: '10px 12px', textAlign: 'left', color: active ? 'var(--text-primary)' : 'var(--text-muted)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>
                        {h.key ? (
                          <button onClick={() => onSortChange(h.key)}
                            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, font: 'inherit', color: 'inherit', textTransform: 'inherit', letterSpacing: 'inherit' }}>
                            {h.label}
                            <span style={{ fontSize: 9, opacity: active ? 1 : 0.35 }}>{active ? (sort.dir === 'desc' ? '▼' : '▲') : '⇅'}</span>
                          </button>
                        ) : h.label}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {profiles.map(p => {
                  const isSel = selected.includes(p.id);
                  return (
                    <React.Fragment key={p.id}>
                      <tr style={{ borderBottom: expandedId === p.id ? 'none' : '1px solid var(--border)', background: isSel ? 'rgba(59,130,246,0.08)' : '' }}
                        onMouseEnter={e => { if (!isSel) e.currentTarget.style.background = 'var(--bg-card-hover)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = isSel ? 'rgba(59,130,246,0.08)' : ''; }}>
                        <td style={{ padding: '10px 12px' }}>
                          <input type="checkbox" style={cbStyle} checked={isSel} onChange={() => setSelected(isSel ? selected.filter(x => x !== p.id) : [...selected, p.id])} />
                        </td>
                        <td style={{ padding: '10px 12px' }}>
                          <div style={{ fontWeight: 500 }}>{p.name}</div>
                          {p.description && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{p.description}</div>}
                        </td>
                        <td style={{ padding: '10px 12px' }}>
                          <ProfileTypeBadge type={p.profile_type} />
                        </td>
                        <td style={{ padding: '10px 12px' }}><span className="mono" style={{ fontSize: 12, color: 'var(--accent-cyan)' }}>{p.upload_limit_m != null ? `${p.upload_limit_m} MB` : '—'}</span></td>
                        <td style={{ padding: '10px 12px' }}><span className="mono" style={{ fontSize: 12, color: 'var(--accent-blue)' }}>{p.download_limit_m != null ? `${p.download_limit_m} MB` : '—'}</span></td>
                        <td style={{ padding: '10px 12px' }}><span className="mono" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{p.session_timeout ? `${p.session_timeout}s` : '—'}</span></td>
                        <td style={{ padding: '10px 12px' }}><span style={{ fontSize: 12, color: 'var(--accent-orange)' }}>{formatDuration(p.expired_duration)}</span></td>
                        <td style={{ padding: '10px 12px' }}>
                          <span style={{ fontSize: 11, padding: '2px 8px', background: (p.custom_attrs?.length > 0) ? 'rgba(139,92,246,0.15)' : 'var(--bg-input)', color: (p.custom_attrs?.length > 0) ? 'var(--accent-purple)' : 'var(--text-muted)', borderRadius: 20, border: `1px solid ${(p.custom_attrs?.length > 0) ? 'rgba(139,92,246,0.3)' : 'var(--border)'}` }}>
                            {p.custom_attrs?.length || 0}
                          </span>
                        </td>
                        <td style={{ padding: '10px 12px' }}><Badge status={p.status} /></td>
                        <td style={{ padding: '10px 12px' }}><SyncBadge status={p.sync_status} /></td>
                        <td style={{ padding: '10px 12px' }}>
                          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                            <Button size="sm" variant="ghost" onClick={() => setExpandedId(expandedId === p.id ? null : p.id)}>
                              {expandedId === p.id ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                            </Button>
                            <ProfileMenuButton p={p} openMenuId={openMenuId} setOpenMenuId={setOpenMenuId} onlyDeleted={onlyDeleted} syncingId={syncingId} handleRestore={handleRestore} handleSync={handleSync} openEdit={openEdit} setDeleteConfirm={setDeleteConfirm} setDeletePermanent={setDeletePermanent} isSuperadmin={isSuperadmin} />
                          </div>
                        </td>
                      </tr>
                      {expandedId === p.id && (
                        <tr style={{ borderBottom: '1px solid var(--border)' }}>
                          <td colSpan={11} style={{ padding: '0 16px 14px 44px', background: 'var(--bg-input)' }}>
                            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', paddingTop: 12 }}>RADIUS Attributes</div>
                            {!p.custom_attrs || p.custom_attrs.length === 0 ? (
                              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Tidak ada custom attribute</span>
                            ) : (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                                {p.custom_attrs.map((a, i) => (
                                  <div key={i} style={{ display: 'flex', gap: 10, padding: '6px 10px', background: 'var(--bg-card)', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 12 }}>
                                    <span style={{ color: 'var(--accent-orange)', minWidth: 80 }}>[{a.type}]</span>
                                    <span className="mono" style={{ color: 'var(--text-primary)', flex: 1 }}>{a.attribute}</span>
                                    <span style={{ color: 'var(--text-muted)', minWidth: 28 }}>{a.op}</span>
                                    <span className="mono" style={{ color: 'var(--accent-cyan)', flex: 1 }}>{a.value}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                            {p.last_synced_at && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 10 }}>Last synced: {new Date(p.last_synced_at).toLocaleString('id-ID')}{p.sync_error && <span style={{ color: 'var(--accent-red)', marginLeft: 8 }}>Error: {p.sync_error}</span>}</div>}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div style={{ padding: '0 16px' }}><Pagination page={page} totalPages={totalPages} total={total} onChange={setPage} perPage={perPage} onPerPageChange={n => { setPerPage(n); setPage(1); }} /></div>
      </Card>

      {/* Create/Edit Modal */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editTarget ? 'Edit Profile' : 'Tambah Profile'} width={620}>
        {formError && <Alert type="error" message={formError} />}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {/* Profile Type */}
          <div style={{ gridColumn: '1/-1' }}>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500, marginBottom: 8 }}>Profile Type</div>
            <div style={{ display: 'flex', gap: 8 }}>
              {[['regular','Regular'],['suspended','Suspended'],['expired','Expired']].map(([val, label]) => (
                <label key={val} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', padding: '6px 14px', borderRadius: 'var(--radius)', border: `1px solid ${form.profile_type === val ? (val === 'regular' ? 'var(--accent-blue)' : val === 'suspended' ? 'var(--accent-orange)' : 'var(--accent-red)') : 'var(--border)'}`, background: form.profile_type === val ? (val === 'regular' ? 'rgba(59,130,246,0.1)' : val === 'suspended' ? 'rgba(245,158,11,0.1)' : 'rgba(239,68,68,0.1)') : 'var(--bg-input)', color: form.profile_type === val ? (val === 'regular' ? 'var(--accent-blue)' : val === 'suspended' ? 'var(--accent-orange)' : 'var(--accent-red)') : 'var(--text-secondary)', transition: 'all 0.15s', fontWeight: form.profile_type === val ? 600 : 400 }}>
                  <input type="radio" name="profile_type" value={val} checked={form.profile_type === val} onChange={() => sf('profile_type', val)} style={{ accentColor: 'currentColor' }} />
                  {label}
                </label>
              ))}
            </div>
          </div>
            <Input label="Name *" value={form.name} onChange={e => sf('name', e.target.value)} placeholder="paket-10mbps" />
            <Input label="Upload Limit (MB)" type="number" min={0} value={form.upload_limit_m} onChange={e => sf('upload_limit_m', e.target.value)} placeholder="e.g. 10" />
            <Input label="Download Limit (MB)" type="number" min={0} value={form.download_limit_m} onChange={e => sf('download_limit_m', e.target.value)} placeholder="e.g. 10" />
            <Input label="Session Timeout (detik)" type="number" value={form.session_timeout} onChange={e => sf('session_timeout', e.target.value)} placeholder="3600" />
            <Input label="Interim Update Interval (detik)" type="number" value={form.interim_interval} onChange={e => sf('interim_interval', e.target.value)} placeholder="300" />
            <DurationInput label="Expired Duration" value={form.expired_duration} onChange={v => sf('expired_duration', v)} />
            <div style={{ gridColumn: '1/-1' }}><Input label="Description" value={form.description} onChange={e => sf('description', e.target.value)} /></div>
            <Select label="Status" value={form.status} onChange={e => sf('status', e.target.value)}>
              <option value="active">Active</option><option value="inactive">Inactive</option>
            </Select>
            <div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500, marginBottom: 8 }}>Service Type</div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {[['', 'None'], ['ppp', 'PPP'], ['hotspot', 'Hotspot'], ['device_login', 'Device Login']].map(([val, label]) => (
                  <label key={val} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', padding: '5px 10px', borderRadius: 'var(--radius)', border: `1px solid ${form.service_type === val ? 'var(--accent-blue)' : 'var(--border)'}`, background: form.service_type === val ? 'rgba(59,130,246,0.1)' : 'var(--bg-input)', color: form.service_type === val ? 'var(--accent-blue)' : 'var(--text-secondary)', transition: 'all 0.15s' }}>
                    <input type="radio" name="profile_service_type" value={val} checked={form.service_type === val} onChange={() => sf('service_type', val)} style={{ accentColor: 'var(--accent-blue)' }} />
                    {label}
                  </label>
                ))}
              </div>
            </div>
            <Input label="Shared User (0=unlimited)" type="number" value={form.shared_user} onChange={e => sf('shared_user', e.target.value)} placeholder="0" />
            <Select label="Suspended Profile" value={form.suspended_profile_id} onChange={e => sf('suspended_profile_id', e.target.value ? +e.target.value : '')}>
              <option value="">— Tidak ada —</option>
              {suspendedProfiles.filter(p => p.id !== editTarget?.id).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </Select>
            <Select label="Expired Profile" value={form.expired_profile_id} onChange={e => sf('expired_profile_id', e.target.value ? +e.target.value : '')}>
              <option value="">— Tidak ada —</option>
              {expiredProfiles.filter(p => p.id !== editTarget?.id).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </Select>
          </div>
          {editTarget && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-secondary)', cursor: 'pointer', padding: '8px 0' }}>
              <input type="checkbox" checked={form.disconnect_user || false} onChange={e => sf('disconnect_user', e.target.checked)} style={{ accentColor: 'var(--accent-orange)' }} />
              <span>Disconnect user yang sedang online setelah update</span>
            </label>
          )}
          {/* custom_attrs editor */}
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>RADIUS Attributes <span style={{ color: 'var(--accent-purple)', marginLeft: 6 }}>({form.custom_attrs.length})</span></span>
              <Button size="sm" variant="ghost" onClick={addAttr}><Plus size={12} /> Tambah</Button>
            </div>
            {form.custom_attrs.length === 0 ? (
              <div style={{ padding: '10px 14px', background: 'var(--bg-input)', borderRadius: 'var(--radius)', border: '1px dashed var(--border)', textAlign: 'center', fontSize: 12, color: 'var(--text-muted)' }}>
                Kosong — dikirim sebagai <span className="mono" style={{ color: 'var(--accent-purple)' }}>custom_attrs: []</span>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr 58px 1fr 22px', gap: 6 }}>
                  {['Type','Attribute','Op','Value',''].map(h => <span key={h} style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>{h}</span>)}
                </div>
                {form.custom_attrs.map((attr, i) => (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '90px 1fr 58px 1fr 22px', gap: 6, alignItems: 'center' }}>
                    <select value={attr.type} onChange={e => updateAttr(i, 'type', e.target.value)} style={{ background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '6px', color: 'var(--text-primary)', fontSize: 12, cursor: 'pointer' }}>
                      <option value="reply">reply</option><option value="check">check</option>
                    </select>
                    <input value={attr.attribute} onChange={e => updateAttr(i, 'attribute', e.target.value)} placeholder="Mikrotik-Rate-Limit" style={{ background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '6px 8px', color: 'var(--text-primary)', fontSize: 12, outline: 'none', fontFamily: 'var(--font-mono)' }} />
                    <select value={attr.op} onChange={e => updateAttr(i, 'op', e.target.value)} style={{ background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '6px 4px', color: 'var(--text-primary)', fontSize: 12, cursor: 'pointer' }}>
                      {[':=','=','+=','==','!=','>=','<='].map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                    <input value={attr.value} onChange={e => updateAttr(i, 'value', e.target.value)} placeholder="10M/10M" style={{ background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '6px 8px', color: 'var(--text-primary)', fontSize: 12, outline: 'none', fontFamily: 'var(--font-mono)' }} />
                    <button onClick={() => removeAttr(i)} style={{ background: 'none', border: 'none', color: 'var(--accent-red)', cursor: 'pointer', padding: 2, display: 'flex', alignItems: 'center' }}><X size={14} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
          <Button variant="ghost" onClick={() => setModalOpen(false)}>Batal</Button>
          <Button onClick={handleSave} loading={saving}>{editTarget ? 'Simpan' : 'Buat Profile'}</Button>
        </div>
      </Modal>

      {/* Delete Confirm */}
      <Modal open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} title="Hapus Profile" width={380}>
        <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 16 }}>
          Yakin ingin menghapus profile <strong style={{ color: 'var(--accent-red)' }}>{deleteConfirm?.name}</strong>?
        </p>
        {isSuperadmin && (onlyDeleted ? (
          <p style={{ fontSize: 12, color: 'var(--accent-red)', marginBottom: 20 }}>
            ⚠️ Profile ini sudah berada di daftar terhapus, sehingga akan dihapus permanen dan tidak bisa dipulihkan lagi.
          </p>
        ) : (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20, cursor: 'pointer' }}>
            <input type="checkbox" checked={deletePermanent} onChange={e => setDeletePermanent(e.target.checked)} style={{ accentColor: 'var(--accent-red)' }} />
            <span>Hapus permanen <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>(tidak bisa dipulihkan)</span></span>
          </label>
        ))}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={() => setDeleteConfirm(null)}>Batal</Button>
          <Button variant="danger" onClick={handleDelete}>Hapus</Button>
        </div>
      </Modal>

      {/* Bulk Delete */}
      <Modal open={bulkDeleteConfirm} onClose={() => setBulkDeleteConfirm(false)} title="Hapus Bulk Profile" width={380}>
        <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 16 }}>
          Hapus <strong style={{ color: 'var(--accent-red)' }}>{selected.length} profile</strong>?
        </p>
        {isSuperadmin && (onlyDeleted ? (
          <p style={{ fontSize: 12, color: 'var(--accent-red)', marginBottom: 20 }}>
            ⚠️ Profile ini sudah berada di daftar terhapus, sehingga akan dihapus permanen dan tidak bisa dipulihkan lagi.
          </p>
        ) : (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20, cursor: 'pointer' }}>
            <input type="checkbox" checked={bulkDeletePermanent} onChange={e => setBulkDeletePermanent(e.target.checked)} style={{ accentColor: 'var(--accent-red)' }} />
            <span>Hapus permanen <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>(tidak bisa dipulihkan)</span></span>
          </label>
        ))}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={() => setBulkDeleteConfirm(false)}>Batal</Button>
          <Button variant="danger" onClick={handleBulkDelete}>Hapus</Button>
        </div>
      </Modal>

      {/* Bulk Update */}
      <Modal open={bulkUpdateModal} onClose={() => setBulkUpdateModal(false)} title={`Bulk Update — ${selected.length} profile`} width={460}>
        <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>Field kosong tidak akan diubah. <span style={{ color: 'var(--accent-orange)' }}>✗ Name tidak bisa di-bulk update.</span></p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Select label="Status" value={bulkUpdateForm.status} onChange={e => setBulkUpdateForm(f => ({ ...f, status: e.target.value }))}>
              <option value="">— Tidak diubah —</option><option value="active">Active</option><option value="inactive">Inactive</option>
            </Select>
            <Input label="Rate Limit" value={bulkUpdateForm.rate_limit} onChange={e => setBulkUpdateForm(f => ({ ...f, rate_limit: e.target.value }))} placeholder="10M/10M" />
            <Input label="Session Timeout (detik)" type="number" value={bulkUpdateForm.session_timeout} onChange={e => setBulkUpdateForm(f => ({ ...f, session_timeout: e.target.value }))} placeholder="3600" />
            <Input label="Interim Update Interval (detik)" type="number" value={bulkUpdateForm.interim_interval} onChange={e => setBulkUpdateForm(f => ({ ...f, interim_interval: e.target.value }))} placeholder="300" />
            <DurationInput label="Expired Duration" value={bulkUpdateForm.expired_duration} onChange={v => setBulkUpdateForm(f => ({ ...f, expired_duration: v }))} />
          </div>
          <Input label="Description" value={bulkUpdateForm.description} onChange={e => setBulkUpdateForm(f => ({ ...f, description: e.target.value }))} />
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
          <Button variant="ghost" onClick={() => setBulkUpdateModal(false)}>Batal</Button>
          <Button onClick={handleBulkUpdate} loading={bulkUpdating}>Update {selected.length} Profile</Button>
        </div>
      </Modal>
    </div>
  );
}